import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollManager = () => {
    const location = useLocation();
    const { pathname, search } = location;
    const fullPath = pathname + search;
    const scrollPositions = useRef<Record<string, number>>({});
    const isInitialLoad = useRef(true);
    const lastFullPath = useRef(fullPath);

    // Отключаем все автоматические скроллы
    useEffect(() => {
        // Отключаем стандартное поведение браузера
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }

        // Перехватываем навигацию
        const originalPushState = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function(...args) {
            // Сохраняем позицию перед навигацией
            if (lastFullPath.current) {
                scrollPositions.current[lastFullPath.current] = window.scrollY;
                sessionStorage.setItem(`scroll_${lastFullPath.current}`, window.scrollY.toString());
            }
            return originalPushState.apply(this, args);
        };

        window.history.replaceState = function(...args) {
            // Сохраняем позицию перед навигацией
            if (lastFullPath.current) {
                scrollPositions.current[lastFullPath.current] = window.scrollY;
                sessionStorage.setItem(`scroll_${lastFullPath.current}`, window.scrollY.toString());
            }
            return originalReplaceState.apply(this, args);
        };

        return () => {
            window.history.pushState = originalPushState;
            window.history.replaceState = originalReplaceState;
        };
    }, []);

    // Восстановление скролла
    useEffect(() => {
        // Пропускаем первый рендер
        if (isInitialLoad.current) {
            isInitialLoad.current = false;

            // Восстанавливаем из sessionStorage при загрузке
            const savedScroll = sessionStorage.getItem(`scroll_${fullPath}`);
            if (savedScroll) {
                const scrollY = parseInt(savedScroll, 10);
                setTimeout(() => {
                    window.scrollTo({ top: scrollY, behavior: 'auto' });
                }, 50);
            }
            return;
        }

        // Сохраняем позицию для предыдущего пути
        if (lastFullPath.current && lastFullPath.current !== fullPath) {
            scrollPositions.current[lastFullPath.current] = window.scrollY;
            sessionStorage.setItem(`scroll_${lastFullPath.current}`, window.scrollY.toString());
        }

        // Восстанавливаем позицию для текущего пути
        const savedScroll = scrollPositions.current[fullPath];
        if (savedScroll !== undefined) {
            setTimeout(() => {
                window.scrollTo({ top: savedScroll, behavior: 'auto' });
            }, 0);
        } else {
            // Проверяем sessionStorage
            const sessionSaved = sessionStorage.getItem(`scroll_${fullPath}`);
            if (sessionSaved) {
                const scrollY = parseInt(sessionSaved, 10);
                setTimeout(() => {
                    window.scrollTo({ top: scrollY, behavior: 'auto' });
                }, 0);
            } else {
                // Новая страница - скроллим вверх
                window.scrollTo({ top: 0, behavior: 'auto' });
            }
        }

        lastFullPath.current = fullPath;
    }, [fullPath]);

    // Сохраняем скролл при событии скролла
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const handleScroll = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                scrollPositions.current[fullPath] = window.scrollY;
                sessionStorage.setItem(`scroll_${fullPath}`, window.scrollY.toString());
            }, 150);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
        };
    }, [fullPath]);

    return null;
};

export default ScrollManager;